import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from '@inertiajs/react';

type ButtonVariant = 'solid' | 'ghost';

type ButtonProps = {
  variant: ButtonVariant;
  block?: boolean;
  as?: 'button' | 'link';
  href?: string;
  loading?: boolean;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  children: ReactNode;
  onClick?: () => void;
  className?: string;
};

const variantClassName: Record<ButtonVariant, string> = {
  solid: 'border-transparent bg-primary-deep text-[color:var(--color-on-primary)] hover:bg-ink',
  ghost: 'border-line text-ink hover:border-primary hover:text-primary-deep',
};

/**
 * Solid/ghost action button matching the mockup's `.btn`/`.btn-solid`/`.btn-ghost`.
 * `as="link"` renders an Inertia `Link` (for `/contact`, `/services`, etc.);
 * `as="button"` (default) renders a native `<button>` for form submits/handlers.
 */
export const Button = ({
  variant,
  block = false,
  as = 'button',
  href,
  loading = false,
  type = 'button',
  children,
  onClick,
  className,
}: ButtonProps) => {
  const classes = [
    'inline-flex min-h-[46px] items-center justify-center gap-2 rounded-sm border px-[22px] text-sm font-semibold tracking-[.03em] transition-[background-color,border-color,color] duration-[180ms]',
    variantClassName[variant],
    block ? 'w-full' : '',
    loading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {loading ? <ButtonSpinner /> : null}
      <span>{children}</span>
    </>
  );

  if (as === 'link') {
    return (
      <Link
        href={href ?? '#'}
        className={loading ? `${classes} pointer-events-none` : classes}
        aria-disabled={loading || undefined}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={loading}
      aria-busy={loading || undefined}
    >
      {content}
    </button>
  );
};

const ButtonSpinner = () => (
  <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"
    />
  </svg>
);
